import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./App.css";
import ZoomImage from "./ZoomImage";
import LoaderOverlay from "./LoaderOverlay";


const App = () => {
  const [board, setBoard] = useState(null);
  const [boardId, setBoardId] = useState(1); // Id par défaut
  const [modalContent, setModalContent] = useState(null); // Contenu pour la modale
  const [showModal, setShowModal] = useState(false);       // Contrôle de la modale
  const [inputMessage, setInputMessage] = useState("");    // Message saisi
  const [currentTileId, setCurrentTileId] = useState(null);// Id de la tuile cliquée
  const [isLoading, setIsLoading] = useState(false);       // Indicateur de chargement
  const [serverMessage, setServerMessage] = useState("");  // Message de retour serveur
  const [generatingTileId, setGeneratingTileId] = useState(null);


  // -- Gestion du zoom --
  const [showZoom, setShowZoom] = useState(false);
  const [zoomTileRect, setZoomTileRect] = useState(null);
  const [zoomTileUrl, setZoomTileUrl] = useState("");
  const [zoomTileComment, setZoomTileComment] = useState(null);

  // Référence vers la grille (si besoin de calculs) :
  const gridRef = useRef(null);

  // On va stocker une ref pour chaque tuile afin de récupérer leur position (boundingRect)
  const tileRefs = useRef({});

  // Charge les données du tableau au démarrage
  useEffect(() => {
    fetchBoardData(boardId);
  }, [boardId]);

  const fetchBoardData = async (id) => {
    try {
      const response = await axios.get(
        `https://test-springo92.herokuapp.com/api/image-boards/${id}`,
        {
          headers: {
            keyUser:
              "9c97854abcb6b46d33148a0def2525618a1ae829d9378f70c03cab7c68115473",
          },
        }
      );
      setBoard(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement du tableau :", error);
    }
  };

  // Au clic sur une tuile
  const handleTileClick = (tile, e) => {
    setCurrentTileId(tile.idTile);

    if (tile.url) {
      // Si la tuile a déjà une image => on la zoom
      const tileRect = e.currentTarget.getBoundingClientRect();
      setZoomTileRect(tileRect);
      setZoomTileUrl(tile.url);
      setShowZoom(true);
      setZoomTileComment(tile.comment);

      // Fermer la modale si jamais elle était ouverte
      setShowModal(false);
      setModalContent(null);
    } else {
      // Si la tuile n'a pas d'image => on ouvre la modale pour saisir un prompt
      setModalContent(null);
      setShowModal(true);
    }
  };

  // Ferme le zoom
  const handleZoomClose = () => {
    setShowZoom(false);
    setZoomTileUrl("");
    setZoomTileRect(null);
  };

  // Quand on envoie un message pour générer une nouvelle image sur la tuile
  const handleSendMessage = async () => {
    if (!currentTileId || !inputMessage.trim()) return;
    setShowModal(false);
    setIsLoading(true);
    setGeneratingTileId(currentTileId);

    setServerMessage("");

    try {
      // Appel au serveur
      const response = await axios.post(
        "https://test-springo92.herokuapp.com/api/image-boards/message",
        {
          tileId: currentTileId,
          message: inputMessage,
        },
        {
          headers: {
            "Content-Type": "application/json",
            keyUser:
              "9c97854abcb6b46d33148a0def2525618a1ae829d9378f70c03cab7c68115473",
          },
        }
      );

      // Le serveur renvoie : { message: "...", imageUrl: "..." }
      const newImageUrl = response.data.imageUrl; // Suppose qu'il s'appelle "imageUrl"
      //setServerMessage(response.data.message || "");

      // Met à jour localement la tuile concernée (sans recharger tout le board)
      setBoard((prevBoard) => {
        if (!prevBoard) return prevBoard;
        const updatedTiles = prevBoard.tiles.map((tile) => {
          if (tile.idTile === currentTileId) {
            return {
              ...tile,
              url: newImageUrl, // On met la nouvelle URL
              comment: inputMessage, // On définit le commentaire
            };
          }
          return tile;
        });
        return { ...prevBoard, tiles: updatedTiles };
      });

      // On ferme la modale
      setShowModal(false);

      // On attend le rafraîchissement du DOM (setTimeout 0)
      setTimeout(() => {
        // Récupère la référence DOM de la tuile qu'on vient d'actualiser
        const tileElement = tileRefs.current[currentTileId];
        if (tileElement && newImageUrl) {
          // Calcule sa position
          const tileRect = tileElement.getBoundingClientRect();
          // Lance le zoom
          setZoomTileRect(tileRect);
          setZoomTileUrl(newImageUrl);
          setZoomTileComment(inputMessage);
          setShowZoom(true);
        }
      }, 0);
    } catch (error) {
      setServerMessage(
        error.response?.data?.message || "Erreur lors de l'envoi du message."
      );
    } finally {
      setGeneratingTileId(null);
      setIsLoading(false);
      setInputMessage("");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setModalContent(null);
  };

  return (
    <div className="App">
      <h1>PAIxel</h1>
      <label hidden>
        Sélectionnez un tableau :{" "}
        <input
          type="number"
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
          min="1"
        />
      </label>

      {board ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <div>
            <h2 style={{ textAlign: "center" }}>{board.title}</h2>
            <div
              ref={gridRef}
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${board.width}, 40px)`,
                gap: "1px",
                margin: "0 auto",
              }}
            >
              {board.tiles.map((tile) => {
                // On vérifie si c'est la tuile en cours de génération
                const isGenerating = (tile.idTile === generatingTileId);

                return (
                  <div
                    key={tile.idTile}
                    ref={(el) => {
                      tileRefs.current[tile.idTile] = el;
                    }}
                    className={isGenerating ? "tile tile-bounce" : "tile"}
                    style={{
                      position: "relative",          // Pour superposer l'image et l'overlay
                      width: "40px",
                      height: "40px",
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      overflow: "hidden",            // Cache le débordement de l'overlay
                    }}
                    title={tile.comment}
                    onClick={(e) => handleTileClick(tile, e)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.3)";
                      e.currentTarget.style.zIndex = "999";
                      e.currentTarget.style.border = "1px solid black";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.zIndex = "0";
                      e.currentTarget.style.border = "1px solid rgb(204, 204, 204)"; // Remplace par la bordure normale si nécessaire
                    }}


                  >
                    {/* Si une image est dispo, on l'affiche en position absolue */}
                    {tile.url && (
                      <img
                        src={tile.url}
                        alt={tile.comment}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}

                    {/* Superposition de la couleur par défaut de la tuile */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        // Si y a une URL => opacité ~0.5, sinon opacité 1 pour ne pas rendre
                        // la tuile "transparente" quand il n'y a pas d'image.
                        backgroundColor: tile.defaultColor,
                        opacity: tile.url ? 0.5 : 1,
                      }}
                    />
                  </div>

                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <p>Chargement du tableau...</p>
      )}

      {/* Loader overlay si isLoading est true */}
      {isLoading && <LoaderOverlay />}

      {/* Affiche un message du serveur s'il existe */}
      {serverMessage && (
        <div className="server-message">
          <p>{serverMessage}</p>
        </div>
      )}

      {/* Modal pour saisir le message (si la tuile n'a pas d'image) */}
      {showModal && (
        <div className={`modal-overlay ${showModal ? "show" : ""}`} onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeModal}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                fontSize: "24px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#333",
              }}
            >
              &times;
            </button>
            {modalContent || (
              <div>
                <p
                  style={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "16px",
                    fontWeight: 400
                  }}
                >
                  Que voulez-vous voir s'afficher dans cette tuile ?
                </p>
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Saisissez votre message ici..."
                  style={{
                    width: "90%",
                    maxWidth: "500px",
                    height: "80px",
                    padding: "10px 15px",
                    fontSize: "16px",
                    fontFamily: "Arial, sans-serif",
                    border: "1px solid #ccc",
                    borderRadius: "10px",
                    outline: "none",
                    resize: "none",
                    backgroundColor: "#f9f9f9",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  style={{
                    marginTop: "20px",
                    padding: "10px 20px",
                    backgroundColor: "#007BFF",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {isLoading ? "Envoi en cours..." : "Envoyer"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ZoomImage pour l'animation plein écran de l'image existante */}
      {showZoom && zoomTileRect && zoomTileUrl && (
        <ZoomImage
          imageUrl={zoomTileUrl}
          tileRect={zoomTileRect}
          onClose={handleZoomClose}
          comment={zoomTileComment}  // on passe la valeur ici
        />
      )}

    </div>
  );
};

export default App;
